// A Canvas2D surface composited inside MapLibre's WebGL frame. Drawing an
// ordinary DOM canvas over the map lets Safari present the two surfaces at
// different camera frames during a gesture; a custom layer makes their final
// pixels part of the same framebuffer.

import type { CustomLayerInterface, Map as MapLibreMap } from 'maplibre-gl';

type LayerMap = Readonly<Pick<MapLibreMap, 'getCanvas' | 'triggerRepaint'>>;
export type CanvasLayerPaint = 'skip' | 'reuse' | 'upload';
type CanvasGl = Readonly<
  Pick<
    WebGL2RenderingContext,
    | 'ARRAY_BUFFER'
    | 'CLAMP_TO_EDGE'
    | 'COMPILE_STATUS'
    | 'FLOAT'
    | 'FRAGMENT_SHADER'
    | 'LINEAR'
    | 'LINK_STATUS'
    | 'RGBA'
    | 'STATIC_DRAW'
    | 'TEXTURE0'
    | 'TEXTURE_2D'
    | 'TEXTURE_MAG_FILTER'
    | 'TEXTURE_MIN_FILTER'
    | 'TEXTURE_WRAP_S'
    | 'TEXTURE_WRAP_T'
    | 'TRIANGLE_STRIP'
    | 'UNPACK_FLIP_Y_WEBGL'
    | 'UNPACK_PREMULTIPLY_ALPHA_WEBGL'
    | 'UNSIGNED_BYTE'
    | 'VERTEX_SHADER'
    | 'activeTexture'
    | 'attachShader'
    | 'bindBuffer'
    | 'bindTexture'
    | 'bindVertexArray'
    | 'bufferData'
    | 'compileShader'
    | 'createBuffer'
    | 'createProgram'
    | 'createShader'
    | 'createTexture'
    | 'createVertexArray'
    | 'deleteBuffer'
    | 'deleteProgram'
    | 'deleteShader'
    | 'deleteTexture'
    | 'deleteVertexArray'
    | 'drawArrays'
    | 'enableVertexAttribArray'
    | 'getAttribLocation'
    | 'getProgramInfoLog'
    | 'getProgramParameter'
    | 'getShaderInfoLog'
    | 'getShaderParameter'
    | 'getUniformLocation'
    | 'linkProgram'
    | 'pixelStorei'
    | 'shaderSource'
    | 'texImage2D'
    | 'texParameteri'
    | 'texSubImage2D'
    | 'uniform1i'
    | 'useProgram'
    | 'vertexAttribPointer'
  >
>;

interface Resources {
  readonly program: WebGLProgram;
  readonly texture: WebGLTexture;
  readonly buffer: WebGLBuffer;
  readonly vao: WebGLVertexArrayObject;
  readonly sampler: WebGLUniformLocation;
}

function shader(gl: CanvasGl, type: number, source: string): WebGLShader {
  const value = gl.createShader(type);
  if (value === null) throw new Error('Could not create map canvas shader');
  gl.shaderSource(value, source);
  gl.compileShader(value);
  if (gl.getShaderParameter(value, gl.COMPILE_STATUS) !== true) {
    const message = gl.getShaderInfoLog(value) ?? 'unknown shader error';
    gl.deleteShader(value);
    throw new Error(`Could not compile map canvas shader: ${message}`);
  }
  return value;
}

function resources(gl: CanvasGl): Resources {
  const vertex = shader(
    gl,
    gl.VERTEX_SHADER,
    `#version 300 es
in vec2 a_position;
out vec2 v_texture;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  v_texture = (a_position + 1.0) * 0.5;
}`,
  );
  const fragment = shader(
    gl,
    gl.FRAGMENT_SHADER,
    `#version 300 es
precision mediump float;
uniform sampler2D u_texture;
in vec2 v_texture;
out vec4 color;
void main() {
  color = texture(u_texture, v_texture);
}`,
  );
  const program = gl.createProgram();
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (gl.getProgramParameter(program, gl.LINK_STATUS) !== true) {
    const message = gl.getProgramInfoLog(program) ?? 'unknown link error';
    gl.deleteProgram(program);
    throw new Error(`Could not link map canvas program: ${message}`);
  }
  const texture = gl.createTexture();
  const buffer = gl.createBuffer();
  const vao = gl.createVertexArray();
  const sampler = gl.getUniformLocation(program, 'u_texture');
  const position = gl.getAttribLocation(program, 'a_position');
  if (sampler === null || position < 0) throw new Error('Could not allocate map canvas layer');
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindVertexArray(null);
  return { program, texture, buffer, vao, sampler };
}

export function mapCanvasLayer(
  id: string,
  canvas: HTMLCanvasElement,
  paint: () => CanvasLayerPaint,
): CustomLayerInterface {
  let owned: Resources | null = null;
  let width = 0;
  let height = 0;

  const forget = (): void => {
    owned = null;
    width = 0;
    height = 0;
  };

  return {
    id,
    type: 'custom',
    renderingMode: '2d',
    onAdd(_owner: LayerMap, gl: CanvasGl) {
      owned = resources(gl);
    },
    render(gl: CanvasGl) {
      const action = paint();
      if (action === 'skip' || canvas.width === 0 || canvas.height === 0) return;
      const restored = owned === null;
      owned ??= resources(gl);
      const current = owned;
      gl.useProgram(current.program);
      gl.bindVertexArray(current.vao);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, current.texture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
      if (restored || width !== canvas.width || height !== canvas.height) {
        width = canvas.width;
        height = canvas.height;
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
      } else if (action === 'upload') {
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
      }
      gl.uniform1i(current.sampler, 0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      gl.bindVertexArray(null);
    },
    onRemove(_owner: LayerMap, gl: CanvasGl) {
      if (owned !== null) {
        gl.deleteProgram(owned.program);
        gl.deleteTexture(owned.texture);
        gl.deleteBuffer(owned.buffer);
        gl.deleteVertexArray(owned.vao);
      }
      forget();
    },
  };
}

// MapLibre rebuilds its serialized style after WebGL context loss, but custom
// layers are deliberately not serialized. Re-add this layer after that style
// is ready so a recovered Safari tab retains its trains and place markers.
export function installMapCanvasLayer(map: MapLibreMap, layer: Readonly<CustomLayerInterface>): void {
  const install = (): void => {
    if (!map.getLayer(layer.id)) map.addLayer(layer);
  };
  map.on('webglcontextrestored', () => {
    if (map.isStyleLoaded() === true) install();
    else map.once('style.load', install);
  });
  install();
}
